import { useMemo, useState, type ReactNode } from 'react';
import { DataTable, rememberPageSize, storedPageSize, type Column } from './DataTable';
import type { Paged } from '../lib/api';

/**
 * Paginated table over an array already held in memory.
 *
 * Some endpoints deliberately return a whole set rather than a page: the enrolment screen
 * must post every course back with its tick state, and the teacher statement's totals are
 * computed across all rows. Paging those on the server would break them.
 *
 * They are still tables, and the requirement is that every table paginates with a page-size
 * selector — so this pages the array on the client while the caller keeps the full list.
 * Same UI, same controls, no behavioural difference for the user.
 */
export function LocalDataTable<T>({
  rows,
  columns,
  rowKey,
  loading = false,
  error = null,
  emptyMessage,
  toolbar,
  above,
  initialPageSize,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  toolbar?: ReactNode;
  /** Rendered above the toolbar, inside the table's frame. Used for the filter bar. */
  above?: ReactNode;
  initialPageSize?: number;
}) {
  const [page, setPage] = useState(1);
  // Falls back to the shared preference, so a client-paged table matches the server-paged
  // ones. A caller can still force a size when the screen genuinely needs one.
  const [pageSize, setPageSize] = useState(() => initialPageSize ?? storedPageSize());

  const paged = useMemo<Paged<T>>(() => {
    const totalCount = rows.length;
    const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;

    // Clamp: deleting the last row of the last page would otherwise strand the user on an
    // empty page with no way back except the pager.
    const safePage = totalPages === 0 ? 1 : Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;

    return {
      items: rows.slice(start, start + pageSize),
      page: safePage,
      pageSize,
      totalCount,
      totalPages,
      hasPrevious: safePage > 1,
      hasNext: safePage < totalPages,
    };
  }, [rows, page, pageSize]);

  return (
    <DataTable
      columns={columns}
      data={paged}
      loading={loading}
      error={error}
      rowKey={rowKey}
      emptyMessage={emptyMessage}
      toolbar={toolbar}
      above={above}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        rememberPageSize(size);
        setPageSize(size);
        setPage(1);
      }}
    />
  );
}
